import { useEffect } from 'react';
import { useSession, useUser } from '@descope/react-sdk';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import LandingPage from '@/pages/LandingPage';
import Layout from '@/components/Layout';
import Dashboard from '@/pages/dashboard/Dashboard';
import OrganizationView from '@/pages/organization/OrganizationView';
import GithubCallback from '@/pages/github/GithubCallback';
import GitHubLinkCallback from '@/pages/github/GitHubLinkCallback';
import { useFlagsmithIdentity } from '@/hooks/useFlagsmithIdentity';
import { PlatformReleaseProvider } from '@/components/shared/platform/PlatformReleaseProvider';
import { ProfileRestoration } from './components/ProfileRestoration';
import { captureInviteTokenFromUrl } from './lib/inviteTokenPersistence';

export default function App() {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { isUserLoading } = useUser();

  // Persist any `?invite_token` before the signup/login redirect can drop it
  // (KIR-3786). Runs regardless of auth state so a first-time invitee's token
  // survives the round-trip back into the authenticated app.
  useEffect(() => {
    captureInviteTokenFromUrl();
  }, []);

  // Set Flagsmith identity when user/org context is available
  useFlagsmithIdentity();

  if (isSessionLoading || isUserLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated ? (
        <PlatformReleaseProvider>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<ProfileRestoration />} />
              <Route path="dashboard/*" element={<Dashboard />} />
              <Route path=":organizationId/*" element={<OrganizationView />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
            {/* GitHub integration callback route (outside Layout) */}
            <Route
              path="/integrations/github/callback"
              element={<GithubCallback />}
            />
            {/* GitHub account linking callback route (outside Layout) */}
            <Route
              path="/integrations/github/link/callback"
              element={<GitHubLinkCallback />}
            />
          </Routes>
        </PlatformReleaseProvider>
      ) : (
        <LandingPage />
      )}
      <Toaster />
    </>
  );
}
